/**
 * @file ShopTimeZoneField.tsx
 * @description Поле «Часовой пояс» формы «Магазин» (phase19.31, SC58).
 *
 * Выбор с поиском (`Combobox`; нативный select запрещён): сначала группа
 * «Россия», затем «Другие»; пункт — «Екатеринбург (+05:00)», под ним
 * IANA-имя (по нему тоже ищется: «Asia/…»). Подсказка под полем объясняет,
 * на что влияет пояс; ошибка сервера `shop_timezone_invalid` встаёт на её
 * место.
 */

import { useMemo } from "react";

import { Combobox, type ComboboxOption } from "../../../components/ui/combobox";
import { Label } from "../../../components/ui/label";
import type { ShopTimeZoneOption } from "../../../lib/api-shop-settings";

interface Props {
  value: string;
  options: ShopTimeZoneOption[];
  onChange: (zone: string) => void;
  error?: string;
  disabled?: boolean;
  /** Обязательное поле — звёздочка у подписи (форма «Новый магазин», PL4). */
  required?: boolean;
}

/** Подсказка под полем (SC58): пояс меняет только показ времени. */
const TIME_ZONE_HINT = "Время в кабинете показывается по этому поясу";

/** «Екатеринбург (+05:00)». */
function timeZoneLabel(zone: ShopTimeZoneOption): string {
  return `${zone.label} (${zone.offset})`;
}

export function ShopTimeZoneField({ value, options, onChange, error, disabled, required }: Props) {
  const items = useMemo<ComboboxOption[]>(
    () =>
      options.map((zone) => ({
        value: zone.id,
        label: timeZoneLabel(zone),
        // IANA-имя подписью — только если название другое (иначе дубль).
        description: zone.label === zone.id ? undefined : zone.id,
        group: zone.group,
      })),
    [options],
  );
  // Пояса магазина нет в списке (старое имя) — покажем его как есть, не пустоту.
  const known = items.some((item) => item.value === value);
  const list = known || !value ? items : [{ value, label: value }, ...items];

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="shop-time-zone">
        Часовой пояс
        {required ? <span aria-hidden> *</span> : null}
      </Label>
      <Combobox
        id="shop-time-zone"
        options={list}
        value={value || null}
        onChange={(next) => {
          if (next) onChange(next);
        }}
        placeholder="Выберите пояс"
        searchPlaceholder="Город или пояс"
        emptyText="Такого пояса нет"
        invalid={!!error}
        describedBy="shop-time-zone-hint"
        disabled={disabled}
        className="max-w-[var(--form-field-max,45rem)]"
      />
      <p id="shop-time-zone-hint" className={error ? "type-caption text-[var(--destructive-text)]" : "type-caption text-on-surface-variant"}>
        {error ?? TIME_ZONE_HINT}
      </p>
    </div>
  );
}
